import pick from 'lodash.pick';
import { Component } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { isAddingCreditCard } from '../../reducers/checkoutPaymentReducer';
import { connect } from 'react-redux';
import {
    creditCardDeleted,
    addCreditCard,
    selectCreditCard,
    verifyCreditCard,
    creditCardFormCanceled,
} from '../../actions/checkoutPaymentActions';
import { paymentMethodIsPermissible } from '../helpers/paymentMethodIsPermissible';
import CheckoutMutationWrapper from '../../mutations/CheckoutMutationWrapper';
import { creditCardFields, formCreditCardExpiryValue } from '../../helpers/checkoutForms';
import { STATES } from '../../constants/checkoutConstants';

import { CheckoutServerVarsContext } from '../../components/shared/CheckoutServerVarsContext';

// components
import CheckoutCreditCardList from './CheckoutCreditCardList';
import CheckoutAddCreditCard from './CheckoutAddCreditCard';
import CheckoutConfirmModal from '../../shared/CheckoutConfirmModal';
class CheckoutCreditCardListWrapper extends Component {
    constructor(props) {
        super(props);
        this.onSelect = this.onSelect.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onCancel = this.onCancel.bind(this);
        this.onDischarge = this.onDischarge.bind(this);
        this.onConfirm = this.onConfirm.bind(this);
        this.onClose = this.onClose.bind(this);
        this.state = {
            isOpen: false,
            selectedCardToken: null,
        };
    }
    componentDidMount() {
        this.mounted = true;
    }
    componentWillUnmount() {
        this.mounted = false;
    }
    deleteCreditCard() {
        // delete credit card
        const { selectedCardToken } = this.state;
        const { relay, buyer, paymentMethodToken, onCreditCardDeleted } = this.props;
        const { isMobile } = this.context;

        const walletPaymentMethods = buyer?.wallet?.paymentMethods || [];
        const walletPaymentMethod = walletPaymentMethods.find(
            ({ paymentMethod }) => paymentMethod.token === selectedCardToken
        );

        CheckoutMutationWrapper.commit(
            'UserDeletePaymentMethodMutation',
            relay.environment,
            {
                userId: buyer.serviceId,
                paymentMethodType: 'CREDIT_CARD',
                paymentMethodKey: walletPaymentMethod?.paymentMethod?.hashedCardNumber,
            },
            isMobile
        )
            .then(response => {
                const { user } = response?.userDeletePaymentMethod || {};
                const hasCreditCards = user?.wallet?.hasCreditCards;
                const isSelectedCardDeleted = paymentMethodToken === selectedCardToken;
                onCreditCardDeleted({
                    creditCardState: hasCreditCards ? STATES.STATE_SAVED : STATES.STATE_NEW,
                    paymentMethodToken: isSelectedCardDeleted
                        ? user?.firstValidPaymentMethod?.token || null
                        : paymentMethodToken,
                    isSelectedCardDeleted,
                });
                this.onClose();
            })
            .catch(this.onClose);
    }
    onSelect(event, selectedToken, isSelected) {
        // trigger on credit card selection
        if (isSelected) {
            return;
        }
        const {
            buyer,
            shippingAddressId,
            onAddCreditCard,
            onSelectCreditCard,
            onVerifyCreditCard,
            onCreditCardMethodSelect,
        } = this.props;
        const walletPaymentMethods = buyer?.wallet?.paymentMethods || [];
        const walletPaymentMethod =
            selectedToken &&
            walletPaymentMethods.find(({ paymentMethod }) => paymentMethod.token === selectedToken);
        const creditCard = walletPaymentMethod?.paymentMethod;
        const isExpired = !!creditCard?.isExpired;

        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (!isExpired) {
            const isPermissibleCard = paymentMethodIsPermissible(
                walletPaymentMethod,
                shippingAddressId
            );

            onCreditCardMethodSelect();

            if (!selectedToken) {
                onAddCreditCard();
            } else if (isPermissibleCard) {
                onSelectCreditCard({
                    token: selectedToken,
                    paymentProcessor: creditCard?.paymentProcessor,
                });
            } else {
                const verifyCardForm = pick(creditCard, creditCardFields);
                onVerifyCreditCard({
                    token: selectedToken,
                    verifyCardForm: {
                        ...verifyCardForm,
                        cardExpiry: formCreditCardExpiryValue(creditCard),
                    },
                });
            }
        }
    }
    onDelete(token) {
        // trigger on delete action
        this.setState({
            isOpen: true,
            selectedCardToken: token,
        });
    }
    onCancel() {
        this.props.onValidationError({ isValid: true, errors: {} });
        this.props.onCreditCardFormCanceled();
    }
    onDischarge() {
        // trigger on confirmation modal discharge
        this.onClose();
    }
    onConfirm() {
        // trigger on confirmation modal submission
        this.deleteCreditCard();
    }
    onClose() {
        if (this.mounted) {
            this.setState({ isOpen: false });
        }
    }
    render() {
        const { isOpen } = this.state;
        const {
            viewer,
            buyer,
            isAdding,
            checkoutPaymentErrors,
            onValidationUpdate,
            onValidationError,
            deselectCreditCards,
            expandableCardList,
        } = this.props;
        const actions = {
            onDelete: this.onDelete,
            onCancel: this.onCancel,
        };
        const creditCardProps = {
            viewer,
            buyer,
            checkoutPaymentErrors,
            onValidationUpdate,
            onValidationError,
        };
        return (
            <div>
                <CheckoutCreditCardList
                    {...creditCardProps}
                    onSelect={this.onSelect}
                    actions={actions}
                    expandableCardList={expandableCardList}
                />
                <CheckoutAddCreditCard
                    {...creditCardProps}
                    isSelected={isAdding && !deselectCreditCards}
                    onSelect={this.onSelect}
                    onCancel={this.onCancel}
                />
                {isOpen && (
                    <CheckoutConfirmModal
                        isOpen={isOpen}
                        onDischarge={this.onDischarge}
                        onConfirm={this.onConfirm}
                        onClose={this.onClose}
                    />
                )}
            </div>
        );
    }
}

CheckoutCreditCardListWrapper.propTypes = {
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    buyer: PropTypes.object.isRequired,
    shippingAddressId: PropTypes.string.isRequired,
    paymentMethodToken: PropTypes.string,
    paymentMethodApiType: PropTypes.string,
    lastValidCreditCardToken: PropTypes.string,
    isAdding: PropTypes.bool.isRequired,
    onAddCreditCard: PropTypes.func.isRequired,
    onSelectCreditCard: PropTypes.func.isRequired,
    onVerifyCreditCard: PropTypes.func.isRequired,
    onCreditCardFormCanceled: PropTypes.func.isRequired,
    onCreditCardDeleted: PropTypes.func.isRequired,
    checkoutPaymentErrors: PropTypes.object.isRequired,
    onValidationUpdate: PropTypes.func.isRequired,
    onValidationError: PropTypes.func.isRequired,
    onCreditCardMethodSelect: PropTypes.func,
    deselectCreditCards: PropTypes.bool,
    expandableCardList: PropTypes.bool,
};

CheckoutCreditCardListWrapper.defaultProps = {
    onCreditCardMethodSelect: () => {},
};

CheckoutCreditCardListWrapper.contextType = CheckoutServerVarsContext;

const ConnectedCheckoutCreditCardListWrapper = connect(
    ({ transaction, payment }) => ({
        shippingAddressId: transaction.shippingAddressId,
        paymentMethodToken: transaction.paymentMethodToken,
        paymentMethodApiType: transaction.paymentMethodApiType,
        lastValidCreditCardToken: payment.lastValidCreditCardToken,
        isAdding: isAddingCreditCard(payment),
        deselectCreditCards: payment.deselectCreditCards,
    }),
    {
        onAddCreditCard: addCreditCard,
        onSelectCreditCard: selectCreditCard,
        onVerifyCreditCard: verifyCreditCard,
        onCreditCardFormCanceled: creditCardFormCanceled,
        onCreditCardDeleted: creditCardDeleted,
    }
)(CheckoutCreditCardListWrapper);

export default createFragmentContainer(ConnectedCheckoutCreditCardListWrapper, {
    viewer: graphql`
        fragment CheckoutCreditCardListWrapper_viewer on Viewer {
            ...CheckoutCreditCardList_viewer
            ...CheckoutAddCreditCard_viewer
        }
    `,
    buyer: graphql`
        fragment CheckoutCreditCardListWrapper_buyer on User {
            ...CheckoutCreditCardList_buyer
            ...CheckoutAddCreditCard_buyer
            serviceId
            wallet {
                hasCreditCards
                paymentMethods {
                    paymentMethod {
                        __typename
                        ... on CreditCard {
                            token
                            isExpired
                            hashedCardNumber
                            paymentProcessor
                        }
                    }
                    ...paymentMethodIsPermissible_paymentMethod
                }
            }
        }
    `,
});
